import { Box } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { CompanyActiveState, EnumHelper } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/framework/base";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { CompanyListItem, ICompanyListItem } from "../../../models/company/companyListItem";
import * as instantMessageService from "../../../models/services/instantMessageService";
import { SelectCompanyDialog } from "../../company/SelectCompanyDialog/SelectCompanyDialog";
import { ActionContainer } from "../../framework/ActionContainer";
import { DialogSettingsCard } from "../../framework/Container/DialogSettingsCard";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { EllipsisTypography } from "../../framework/Typography/EllipsisTypography";

export interface InstantMessagePartnersDialogCompanyListProp {
    isReadOnly?: boolean;
    companyId: string;
    companies: ICompanyListItem[];
    onRemoved: (ids: string[]) => void;
    onAdded: (users: ICompanyListItem[]) => void;
}

interface IInstantMessagePartnersDialogCompanyListState {
    // List
    isLoading: boolean;
    companies: ICompanyListItem[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];

    // Dialog
    showSelectCompanyDialog: boolean;
}

export class InstantMessagePartnersDialogCompanyList extends React.Component<InstantMessagePartnersDialogCompanyListProp, IInstantMessagePartnersDialogCompanyListState> {
    constructor(props: InstantMessagePartnersDialogCompanyListProp) {
        super(props);
        this.state = {
            isLoading: false,
            companies: props.companies ? props.companies.slice(0) : [],
            filter: "",
            sortColumn: "code",
            sortOrderIsAsc: true,
            selectedId: null,
            checkedIds: [],
            showSelectCompanyDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const props = this.props;
        let companies = props.companies ? props.companies.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            companies = companies.filter(i => i.code.toLowerCase().indexOf(lowFilter) > -1 || i.name.toLowerCase().indexOf(lowFilter) > -1);
        }
        CompanyListItem.sortCompanyListItems(companies, column, asc);
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            companies,
        });
    };

    componentDidUpdate(prevProps: InstantMessagePartnersDialogCompanyListProp, prevState: IInstantMessagePartnersDialogCompanyListState) {
        const props = this.props;
        const state = this.state;
        if (Base.equalStrArrays(prevProps.companies ? prevProps.companies.map(i => i.id) : [], props.companies ? props.companies.map(i => i.id) : [])) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: InstantMessagePartnersDialogCompanyListProp, companyIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.companyId);
        result.append("companyIds", companyIds);
        return result;
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const saveData = this.getRemoveSaveData(props, selectedIds);
        // Call server
        const result = await AppUtils.callService(() => instantMessageService.removeInstantMessagePartners(saveData.obj));
        if (!result) return;
        props.onRemoved(selectedIds);
    };
    
    handleAdd = () => {
        this.setState({
            showSelectCompanyDialog: true
        });
    };

    getAddSaveData = (props: InstantMessagePartnersDialogCompanyListProp, companyIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.companyId);
        result.append("companyIds", companyIds);
        return result;
    };

    onSelectCompanyDialogOk = async (companies: ICompanyListItem[]) => {
        const props = this.props;
        if (!companies || companies.length < 1) return;
        this.setState({
            showSelectCompanyDialog: false
        });
        const saveData = this.getAddSaveData(this.props, companies.map(i => i.id));
        // Call server
        const result = await AppUtils.callService(() => instantMessageService.addInstantMessagePartners(saveData.obj));
        if (!result) return;
        props.onAdded(companies);
    };

    onSelectCompanyDialogCancel = () => {
        this.setState({
            showSelectCompanyDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <DialogSettingsCard
                fitToContainerHeight
                title={Translations.InstantMessagePartners}
                cardContent={
                    <ActionContainer
                        leftItems={
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={this.changeFilter} />
                        }
                        rightItems={
                            <>
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddPartner}
                                    removeTooltip={Translations.RemovePartner}
                                    addDisabled={props.isReadOnly}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            </>
                        }
                    />
                }
                content={<>
                    <List
                        height="100%"
                        multiSelect={true}
                        intialized={true}
                        isLoading={state.isLoading}
                        hasMore={false}
                        header={
                            {
                                columns: [
                                    { size: 4, column: "code", content: Translations.Code },
                                    { size: 5, column: "name", content: Translations.Name },
                                    { size: 3, column: "activeState", content: Translations.ActiveState }
                                ]
                            }
                        }
                        lines={
                            state.companies.map((item) => {
                                return {
                                    columns: [
                                        {
                                            size: 4,
                                            column: "code",
                                            content:
                                            <Box display="flex" alignItems="center">
                                                <AvatarImage
                                                    name={item.name}
                                                    image={item.logo}
                                                    tooltip={item.name}
                                                    marginRight=".5rem"
                                                />
                                                <EllipsisTypography>{item.code}</EllipsisTypography>
                                            </Box>
                                        },
                                        { size: 5, column: "name", content: item.name },
                                        { size: 3, column: "activeState", content: item.activeStateStr },
                                    ],
                                    id: item.id,
                                    classes: EnumHelper.isEqual(item.activeState, CompanyActiveState.NotInUse) ? "disabled" : ""
                                };
                            })}
                        noDataMessage={Translations.AddPartners}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={this.handleSetSelectedIds}
                        onSearchLines={this.handleSearchLines}
                    />
                    {state.showSelectCompanyDialog &&
                    <SelectCompanyDialog
                        multiSelect={true}
                        ignoreCompanyIds={props.companies.map(i => i.id).concat([props.companyId])}
                        onOk={this.onSelectCompanyDialogOk}
                        onCancel={this.onSelectCompanyDialogCancel}
                        onGetCompanyListItems={instantMessageService.getInstantMessageCompanyListItems}
                    />
                    }
                </>
                }
            />
        );
    }
}